import { useEffect, useState } from "react";
import { X, ChevronRight } from "lucide-react";
import { PROMOS } from "@/lib/promos";
import PromoCard from "@/components/site/PromoCard";

// Bump this key whenever the promo lineup changes — clears prior dismissal so it shows again.
const STORAGE_KEY = "pda-usa-fall-2026-v1-dismissed";

export default function PromoSplash() {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(STORAGE_KEY) === "1") return;
    const t = setTimeout(() => setOpen(true), 600);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && advance();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index]);

  const advance = () => {
    if (index < PROMOS.length - 1) {
      setIndex(index + 1);
      requestAnimationFrame(() => {
        const el = document.getElementById("promo-splash-scroll");
        if (el) el.scrollTop = 0;
      });
    } else {
      sessionStorage.setItem(STORAGE_KEY, "1");
      setOpen(false);
    }
  };

  if (!open) return null;
  const promo = PROMOS[index];
  const isLast = index === PROMOS.length - 1;

  return (
    <div
      data-testid="promo-splash"
      data-promo-id={promo.id}
      className="fixed inset-0 z-[100] flex items-center justify-center px-3 sm:px-6 py-6 sm:py-10 bg-black/85 backdrop-blur-sm animate-fade-in"
      onClick={advance}
      role="dialog"
      aria-modal="true"
    >
      <div
        id="promo-splash-scroll"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[1100px] max-h-full overflow-y-auto"
      >
        {/* Step indicator */}
        {PROMOS.length > 1 && (
          <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-2 h-8 bg-black/70 border border-white/15 text-[10px] uppercase tracking-[0.2em] text-white/70">
            {PROMOS.map((_, i) => (
              <span
                key={i}
                className={`block w-2 h-2 rounded-full ${i === index ? "bg-[#ff4500]" : "bg-white/25"}`}
                aria-hidden="true"
              />
            ))}
            <span className="ml-1">{index + 1} / {PROMOS.length}</span>
          </div>
        )}

        <button
          onClick={advance}
          aria-label={isLast ? "Close" : "Next promo"}
          data-testid="promo-splash-next"
          className="absolute top-3 right-3 z-10 w-10 h-10 flex items-center justify-center bg-black/70 border border-white/20 text-white hover:bg-[#ff4500] hover:border-[#ff4500] transition-colors"
        >
          {isLast ? <X className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>

        <PromoCard promo={promo} />

        {/* See-next link pinned at the bottom-right of the card (no Skip — X/backdrop handles dismiss) */}
        {!isLast && (
          <div className="absolute bottom-3 right-3 z-10 pointer-events-none">
            <button
              onClick={advance}
              data-testid="promo-splash-show-next"
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] text-[#ffb700] hover:text-[#ff4500] transition-colors pointer-events-auto bg-black/60 px-3 h-8 border border-[#ffb700]/30"
            >
              See next offer <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
